import { h, Component } from 'preact';
import cx from 'classnames';
import { Chart } from 'chart.js';

import { toDecimal } from '../../../../core/decimal';
import { transform } from '../../../../core/price';

import { PotentialCommission } from '../../../../types/dashboard.model';

const CHART_TYPE = 'bar';

const CHART_OPTIONS: Chart.ChartOptions = {
  maintainAspectRatio: false,
  scales: {
    yAxes: [
      {
        stacked: true,
        gridLines: {
          display: false
        },
        display: false
      }
    ], 
    xAxes: [
      {
        // maxBarThickness: 68,
        // categoryPercentage: 1,
        barPercentage: 0.5,
        stacked: true,
        display: false
      }
    ],
  },
  legend: {
    display: false
  },
  showLines: false,
}

const CHART_COLORS = {
  'topaz': '#16af94',
  'darkish-blue': '#00448b',
  'light-peach': '#ffd7b2',
  'mango': '#fea02f',
  'reddish-orange': '#ee590b',
  'milk-chocolate': '#64361e'
}

interface PotentialCommissionWidgetProps {
  salePotentialCommission: PotentialCommission;
  rentalPotentialCommission: PotentialCommission;
}

interface PotentialCommissionWidgetState {

  rentalChart?: Chart;
  saleChart?: Chart;

  isCollapseVisible?: boolean;

}

export class PotentialCommissionWidget extends Component<PotentialCommissionWidgetProps, PotentialCommissionWidgetState> {
  rentalChartNode: any;
  saleChartNode: any;

  constructor(props: PotentialCommissionWidgetProps) {
    super(props);


  }

  componentDidMount() {
    this.createRentalChart();
    this.createSaleChart();
  }

  createRentalChart() {
    let potentialCommission = this.props.rentalPotentialCommission;

    const chart = new Chart(this.rentalChartNode, {
      type: CHART_TYPE,
      data: {
        labels: ['Rental Listings'],
        datasets: [
          // Please note the arrangement is important.
          // Offline
          {
            label: 'Declined (Offline)',
            data: [potentialCommission.offlineDeclined],
            backgroundColor: [CHART_COLORS['milk-chocolate']]
          },
          {
            label: 'Expired (Offline)',
            data: [potentialCommission.offlineExpired],
            backgroundColor: [CHART_COLORS['reddish-orange']]
          },
          {
            label: 'Pending (Offline)',
            data: [potentialCommission.offlinePending],
            backgroundColor: [CHART_COLORS['mango']]
          },
          {
            label: 'Drafts (Offline)',
            data: [potentialCommission.offlineDrafts],
            backgroundColor: [CHART_COLORS['light-peach']]
          },

          // Online
          {
            label: 'Occupied (Online)',
            data: [potentialCommission.onlineOccupied],
            backgroundColor: [CHART_COLORS['darkish-blue']]
          },
          {
            label: 'Active (Online)',
            data: [potentialCommission.onlineActive],
            backgroundColor: [CHART_COLORS['topaz']]
          }       
        ]
      },
      options: CHART_OPTIONS,
    });

    this.setState({
      rentalChart: chart
    });

  }

  createSaleChart() {
    let potentialCommission = this.props.salePotentialCommission;

    const chart = new Chart(this.saleChartNode, {
      type: CHART_TYPE,
      data: {
        labels: ['Sale Listings'],
        datasets: [
          // Please note the arrangement is important.
          // Offline
          {
            label: 'Declined (Offline)',
            data: [potentialCommission.offlineDeclined],
            backgroundColor: [CHART_COLORS['milk-chocolate']]
          },
          {
            label: 'Expired (Offline)',
            data: [potentialCommission.offlineExpired],
            backgroundColor: [CHART_COLORS['reddish-orange']]
          },
          {
            label: 'Pending (Offline)',
            data: [potentialCommission.offlinePending],
            backgroundColor: [CHART_COLORS['mango']]
          },
          {
            label: 'Drafts (Offline)',
            data: [potentialCommission.offlineDrafts],
            backgroundColor: [CHART_COLORS['light-peach']]
          },

          // Online
          {
            label: 'Occupied (Online)',
            data: [potentialCommission.onlineOccupied],
            backgroundColor: [CHART_COLORS['darkish-blue']]
          },
          {
            label: 'Active (Online)',
            data: [potentialCommission.onlineActive],
            backgroundColor: [CHART_COLORS['topaz']]
          }       
        ]
      },
      options: CHART_OPTIONS,
    });

    this.setState({
      saleChart: chart
    });

  }

  toggleCollapsable = () => {
    this.setState({
      isCollapseVisible: !this.state.isCollapseVisible
    });
  }

  getRentalOnlineSum = () => {
    let amount: number = 0;

    amount+= this.props.rentalPotentialCommission.onlineActive;
    amount+= this.props.rentalPotentialCommission.onlineOccupied;

    return transform(amount);
  }

  getRentalTotal = () => {
    let amount: number = 0;

    amount+= this.props.rentalPotentialCommission.onlineActive;
    amount+= this.props.rentalPotentialCommission.onlineOccupied;

    amount+= this.props.rentalPotentialCommission.offlineDeclined;
    amount+= this.props.rentalPotentialCommission.offlineDrafts;
    amount+= this.props.rentalPotentialCommission.offlineExpired;
    amount+= this.props.rentalPotentialCommission.offlinePending;

    return transform(amount);
  }

  getSaleTotal = () => {
    let amount: number = 0;

    amount+= this.props.salePotentialCommission.onlineActive;
    amount+= this.props.salePotentialCommission.onlineOccupied;

    amount+= this.props.salePotentialCommission.offlineDeclined;
    amount+= this.props.salePotentialCommission.offlineDrafts;
    amount+= this.props.salePotentialCommission.offlineExpired;
    amount+= this.props.salePotentialCommission.offlinePending;

    return transform(amount);
  }

  getRentalOfflineSum = () => {
    let amount: number = 0;

    amount+= this.props.rentalPotentialCommission.offlineDeclined;
    amount+= this.props.rentalPotentialCommission.offlineDrafts;
    amount+= this.props.rentalPotentialCommission.offlineExpired;
    amount+= this.props.rentalPotentialCommission.offlinePending;

    return transform(amount);
  }

  getSaleOnlineSum = () => {
    let amount: number = 0;

    amount+= this.props.salePotentialCommission.onlineActive;
    amount+= this.props.salePotentialCommission.onlineOccupied;

    return transform(amount);
  }

  getSaleOfflineSum = () => {
    let amount: number = 0;

    amount+= this.props.salePotentialCommission.offlineDeclined;
    amount+= this.props.salePotentialCommission.offlineDrafts;
    amount+= this.props.salePotentialCommission.offlineExpired;
    amount+= this.props.salePotentialCommission.offlinePending;

    return transform(amount);
  }

  getRentalOnlineLegends = (state: PotentialCommissionWidgetState) => {
    if (state.rentalChart) {
      return (
        state.rentalChart.data.datasets.map(set => {

          if (!set.label.includes('Online')) {
            return;
          }

          return (
            <div className="hi-potential-commission-widget__chart-container__legends__member mb-2">
              <div className="hi-potential-commission-widget__chart-container__legends__oval"
                style={`background-color: ${set.backgroundColor[0]};`}>
              </div>
              <span className="ml-2">
                { toDecimal(Math.floor(set.data[0] as number)) } - { set.label }
              </span>
            </div>
          )
        })
      );
    }
  }

  getRentalOfflineLegends = (state: PotentialCommissionWidgetState) => {
    if (state.rentalChart) {
      return (
        state.rentalChart.data.datasets.map(set => {

          if (!set.label.includes('Offline')) {
            return;
          }

          return (
            <div className="hi-potential-commission-widget__chart-container__legends__member mb-2">
              <div className="hi-potential-commission-widget__chart-container__legends__oval"
                style={`background-color: ${set.backgroundColor[0]};`}>
              </div>
              <span className="ml-2 offline">
              { toDecimal(Math.floor(set.data[0] as number)) } - { set.label }
              </span>
            </div>
          )
        })
      );
    }
  }

  getSaleOnlineLegends = (state: PotentialCommissionWidgetState) => {
    if (state.saleChart) {
      return (
        state.saleChart.data.datasets.map(set => {

          if (!set.label.includes('Online')) {
            return;
          }

          return (
            <div className="hi-potential-commission-widget__chart-container__legends__member mb-2">
              <div className="hi-potential-commission-widget__chart-container__legends__oval"
                style={`background-color: ${set.backgroundColor[0]};`}>
              </div>
              <span className="ml-2">
                { toDecimal(Math.floor(set.data[0] as number)) } - { set.label }
              </span>
            </div>
          )
        })
      );
    }
  }

  getSaleOfflineLegends = (state: PotentialCommissionWidgetState) => {
    if (state.saleChart) {
      return (
        state.saleChart.data.datasets.map(set => {

          if (!set.label.includes('Offline')) {
            return;
          }

          return (
            <div className="hi-potential-commission-widget__chart-container__legends__member mb-2">
              <div className="hi-potential-commission-widget__chart-container__legends__oval"
                style={`background-color: ${set.backgroundColor[0]};`}>
              </div>
              <span className="ml-2 offline">
              { toDecimal(Math.floor(set.data[0] as number)) } - { set.label }
              </span>
            </div>
          )
        })
      );
    }
  }

  render(props, state: PotentialCommissionWidgetState) {
    return(
      <div className="hi-potential-commission-widget hi-widget-collapsable">
        <div className="row hi-widget-collapsable__toggle-row">
          <div className="col-10">
            <h4 className="hi-broker-leaderboard-widget__name">
              Potential Commission (PHP)
            </h4>
          </div>
          <div className="col-2 d-md-none">
            <i className={ cx('fas fa-chevron-down', {
                'visible': state.isCollapseVisible
              })}
              onClick={ this.toggleCollapsable }>
            </i>
          </div>
        </div>

        <div className={ cx('hi-widget-collapsable__body', {
          'visible': state.isCollapseVisible
        }) }>

          <div className="row">

          </div>

          <div className="row mt-5">
            <div className="col-12 col-md-6">

              <div className="hi-potential-commission-widget__chart-container">
                <h4 className="hi-potential-commission-widget__chart-container__total">TOTAL: { this.getRentalTotal() }</h4>
                <canvas ref={node => this.rentalChartNode = node}>
                </canvas>
                <h4 className="hi-potential-commission-widget__chart-container__label">Rental Listings</h4>
              
                <div className="hi-potential-commission-widget__chart-container__legends">
                  <div className="hi-potential-commission-widget__chart-container__legends__group">
                    <h5 className="hi-potential-commission-widget__chart-container__legends__h5 mb-2">
                      Online Sum: { this.getRentalOnlineSum() }
                    </h5>
                    {
                      this.getRentalOnlineLegends(state)
                    }
                    {/* <div className="hi-potential-commission-widget__chart-container__legends__member">
                      
                    </div> */}
                  </div>
                  <div className="hi-potential-commission-widget__chart-container__legends__group mt-3">
                    <h5 className="hi-potential-commission-widget__chart-container__legends__h5 mb-2">
                      Offline Sum: { this.getRentalOfflineSum() }
                    </h5>
                    {
                      this.getRentalOfflineLegends(state)
                    }
                  </div>
                </div>
              
              </div>

            </div>
    
            <div className="col-12 col-md-6 mt-5 mt-md-0">
              <div className="mt-4 mt-md-0">

              </div>

              <div className="hi-potential-commission-widget__chart-container">
                <h4 className="hi-potential-commission-widget__chart-container__total">TOTAL: { this.getSaleTotal() }</h4>
                <canvas ref={node => this.saleChartNode = node}>
                </canvas>
                <h4 className="hi-potential-commission-widget__chart-container__label">Sale Listings</h4>

                <div className="hi-potential-commission-widget__chart-container__legends">
                  <div className="hi-potential-commission-widget__chart-container__legends__group">
                    <h5 className="hi-potential-commission-widget__chart-container__legends__h5 mb-2">
                      Online Sum: { this.getSaleOnlineSum() }
                    </h5>
                    {
                      this.getSaleOnlineLegends(state)
                    }
                    {/* <div className="hi-potential-commission-widget__chart-container__legends__member">
                      
                    </div> */}
                  </div>
                  <div className="hi-potential-commission-widget__chart-container__legends__group mt-3">
                    <h5 className="hi-potential-commission-widget__chart-container__legends__h5 mb-2">
                      Offline Sum: { this.getSaleOfflineSum() }
                    </h5>
                    {
                      this.getSaleOfflineLegends(state)
                    }
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="row mt-5">
            <span className="col-12 hi-potential-commission-widget__note">
              Note: Offline Listings must be online to be available for inquiries.
            </span>
          </div>
        </div>
        
        
      </div>
    );
  }

}